import { formatDisplayAmount } from "../helpers/amountDisplay";
import { WalletAmountInput } from "./WalletAmountInput";
import React from "react";
import { styles } from "../../styles";
import type { TokenInfo } from "../../config";

interface BaseCrossChainTabProps {
  tokens: TokenInfo[];
  inputToken: TokenInfo;
  setInputToken: (t: TokenInfo) => void;
  amount: string;
  setAmount: (v: string) => void;
  inputPrivateBalance: string;
  destinationChain: string;
  setDestinationChain: (v: string) => void;
  destinationAddress: string;
  setDestinationAddress: (v: string) => void;
  destinationResolved: {
    mode: "handle" | "private_address" | "public_eoa" | "unknown";
    handle?: string;
    chainScope?: string;
    recipientKeyMasked?: string;
  } | null;
  destinationResolutionLoading: boolean;
  destinationResolutionError: string | null;
  loading: boolean;
  connected: boolean;
  spendingKeysReady: boolean;
  quoteLoading: boolean;
  quote: any | null;
  quoteReady: boolean;
  quoteError: string | null;
  orderStatus: any | null;
  onBridge: () => void;
  onCancelOrder?: () => void;
}

const DESTINATION_CHAINS = [{ id: "solana", label: "Solana" }];

export function BaseCrossChainTab({
  tokens,
  inputToken,
  setInputToken,
  amount,
  setAmount,
  inputPrivateBalance,
  destinationChain,
  setDestinationChain,
  destinationAddress,
  setDestinationAddress,
  destinationResolved,
  destinationResolutionLoading,
  destinationResolutionError,
  loading,
  connected,
  spendingKeysReady,
  quoteLoading,
  quote,
  quoteReady,
  quoteError,
  orderStatus,
  onBridge,
  onCancelOrder,
}: BaseCrossChainTabProps) {
  const canBridge =
    connected &&
    spendingKeysReady &&
    !loading &&
    parseFloat(amount) > 0 &&
    destinationAddress.trim().length > 0 &&
    !destinationResolutionLoading &&
    !destinationResolutionError &&
    !!destinationResolved &&
    quoteReady;

  return (
    <div>
      <h3 style={{ marginBottom: "16px", color: "#e8eeff" }}>Bridge</h3>
      <p style={{ fontSize: "14px", color: "#b0c4de", marginBottom: "16px" }}>
        Bridge from your shielded balance to a public Solana wallet.
      </p>

      <div style={{ marginBottom: "12px" }}>
        <label style={{ ...styles.label, color: "#b0c4de", fontSize: "13px" }}>
          From token
        </label>
        <select
          value={inputToken.address}
          onChange={(e) => {
            const t = tokens.find((tk) => tk.address === e.target.value);
            if (t) setInputToken(t);
          }}
          style={styles.input}
        >
          {tokens.map((t) => (
            <option key={t.address} value={t.address}>
              {t.symbol}
            </option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: "12px" }}>
        <label style={{ ...styles.label, color: "#b0c4de", fontSize: "13px" }}>
          Amount
        </label>
        <WalletAmountInput
          aria-label="Amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0.0"
          onMax={() => setAmount(inputPrivateBalance)}
          maxDisabled={!inputPrivateBalance || Number(inputPrivateBalance) <= 0}
        />
        {inputPrivateBalance && (
          <div style={{ fontSize: "12px", color: "#b0c4de", marginTop: "4px" }}>
            Private balance: {formatDisplayAmount(inputPrivateBalance)}{" "}
            {inputToken.symbol}
          </div>
        )}
      </div>

      <div style={{ marginBottom: "12px" }}>
        <label style={{ ...styles.label, color: "#b0c4de", fontSize: "13px" }}>
          Destination chain
        </label>
        <select
          value={destinationChain}
          onChange={(e) => setDestinationChain(e.target.value)}
          style={styles.input}
        >
          {DESTINATION_CHAINS.map((chain) => (
            <option key={chain.id} value={chain.id}>
              {chain.label}
            </option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: "12px" }}>
        <label style={{ ...styles.label, color: "#b0c4de", fontSize: "13px" }}>
          Destination Solana wallet address
        </label>
        <input
          style={styles.input}
          type="text"
          value={destinationAddress}
          onChange={(e) => setDestinationAddress(e.target.value)}
          placeholder="Solana wallet address"
        />
        <div style={{ fontSize: "12px", color: "#b0c4de", marginTop: "4px" }}>
          The source spend remains private; the destination address and received
          funds are public on Solana.
        </div>
        {destinationResolutionLoading ? (
          <div style={{ fontSize: "12px", color: "#b0c4de", marginTop: "4px" }}>
            Resolving recipient...
          </div>
        ) : destinationResolutionError ? (
          <div style={{ fontSize: "12px", color: "#ff9aa2", marginTop: "4px" }}>
            {destinationResolutionError}
          </div>
        ) : destinationResolved ? (
          <div style={{ fontSize: "12px", color: "#b0c4de", marginTop: "4px" }}>
            {destinationResolved.mode === "handle"
              ? "Handle"
              : destinationResolved.mode === "public_eoa"
                ? "Solana wallet"
                : "Private address"}{" "}
            resolved: {destinationResolved.handle || destinationAddress.trim()}{" "}
            ({destinationResolved.chainScope || "scoped"}) {"->"}{" "}
            {destinationResolved.recipientKeyMasked}
          </div>
        ) : null}
      </div>

      <div
        style={{
          fontSize: "12px",
          color: "#b0c4de",
          marginBottom: "12px",
          padding: "10px",
          background: "rgba(255,255,255,0.03)",
          borderRadius: "8px",
        }}
      >
        <div style={{ marginBottom: "4px" }}>
          Route mode:{" "}
          <strong style={{ color: "#e8eeff" }}>Transfer (no swap)</strong>
        </div>
        <div>Provider: deBridge DLN</div>
        {quoteLoading ? (
          <div>Fetching quote...</div>
        ) : quote ? (
          <>
            <div>Order ID: {quote.order_id || quote.orderId || "pending"}</div>
            <div>Submit window: ~{quote.submit_within_seconds ?? 30}s</div>
          </>
        ) : (
          <div>
            {quoteError || "Enter amount and destination to fetch quote"}
          </div>
        )}
        {orderStatus ? (
          <div style={{ marginTop: "8px" }}>
            <div>
              Status:{" "}
              <strong style={{ color: "#e8eeff" }}>
                {orderStatus.status || "unknown"}
              </strong>
            </div>
            <div>
              State: {orderStatus.mapped_state || "pending_solver_fill"}
            </div>
            <div>Next action: {orderStatus.next_action || "wait"}</div>
            <div style={{ marginTop: "6px" }}>
              Timeline: created {"->"} pending_solver_fill {"->"} bridge_success
            </div>
          </div>
        ) : null}
      </div>

      <button
        style={{
          ...styles.button,
          background: "#f0f4ff",
          color: "#06060f",
          width: "100%",
          opacity: canBridge ? 1 : 0.6,
        }}
        onClick={onBridge}
        disabled={!canBridge}
      >
        {loading ? "Processing..." : "Bridge to Solana"}
      </button>
      {orderStatus?.mapped_state === "pending_solver_fill" && onCancelOrder ? (
        <button
          style={{
            ...styles.button,
            width: "100%",
            marginTop: "8px",
            background: "rgba(255,255,255,0.08)",
            color: "#e8eeff",
          }}
          onClick={onCancelOrder}
          disabled={loading}
        >
          Cancel Pending Order
        </button>
      ) : null}
    </div>
  );
}
